import React, { memo, useState } from 'react';
import styled from 'styled-components';
import { Popover, Button, Switch, Tooltip, Tag } from 'antd';
import {
  SettingOutlined,
  HolderOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ReloadOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  PushpinOutlined,
  PushpinFilled,
  QuestionCircleOutlined,
  LockOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAtom } from 'jotai';
import FieldTypeIcon, { getFieldTypeMeta } from '../../utils/FieldTypeIcon';
import {
  CollectColumnConfigItem,
  columnSettingsPanelSizeAtom,
} from '../../atoms/CollectFormAtoms';

export type { CollectColumnConfigItem };

interface SortableColumnRowProps {
  item: CollectColumnConfigItem;
  index: number;
  total: number;
  onToggle: (key: string, checked: boolean) => void;
  onTogglePin: (key: string, pinned: boolean) => void;
  onMove: (fromIndex: number, toIndex: number) => void;
}

const SortableColumnRow = memo(({
  item,
  index,
  total,
  onToggle,
  onTogglePin,
  onMove,
}: SortableColumnRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.key });

  const meta = getFieldTypeMeta(item.type, item.key);
  const isLocked = Boolean(item.locked);
  const isPinned = Boolean(item.pinned);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
    opacity: isDragging ? 0.88 : 1,
  };

  return (
    <ColumnRowItem
      ref={setNodeRef}
      style={style}
      $visible={item.visible}
      $isDragging={isDragging}
      $isPinned={isPinned}
      data-testid={`column-setting-row-${item.key}`}
    >
      <DragAndOrderControls>
        <DragHandle
          {...attributes}
          {...listeners}
          title="Kéo thả để sắp xếp vị trí cột"
        >
          <HolderOutlined />
        </DragHandle>
        <OrderArrowGroup>
          <OrderArrowBtn
            type="button"
            disabled={index === 0}
            title="Di chuyển lên"
            data-testid={`column-move-up-${item.key}`}
            onClick={(e) => {
              e.stopPropagation();
              if (index > 0) onMove(index, index - 1);
            }}
          >
            <ArrowUpOutlined />
          </OrderArrowBtn>
          <OrderArrowBtn
            type="button"
            disabled={index === total - 1}
            title="Di chuyển xuống"
            data-testid={`column-move-down-${item.key}`}
            onClick={(e) => {
              e.stopPropagation();
              if (index < total - 1) onMove(index, index + 1);
            }}
          >
            <ArrowDownOutlined />
          </OrderArrowBtn>
        </OrderArrowGroup>
      </DragAndOrderControls>

      <TypeIconBadge $color={meta.color} title={`Kiểu dữ liệu: ${meta.label}`}>
        <FieldTypeIcon
          type={item.type}
          fieldKey={item.key}
          showTooltip={false}
          style={{ marginRight: 0, fontSize: 14 }}
        />
      </TypeIconBadge>

      <ColumnInfo>
        <ColumnTitleRow>
          <ColumnTitle $visible={item.visible} title={item.label}>
            {item.label}
          </ColumnTitle>
          <TypeLabelTag color={meta.tagColor}>
            {meta.label.split(' (')[0]}
          </TypeLabelTag>
          {isLocked && (
            <LockedBadge title="Cột tên luôn hiển thị, không thể tắt">
              <LockOutlined /> Luôn hiện
            </LockedBadge>
          )}
        </ColumnTitleRow>
        <ColumnDescription title={item.description}>
          {item.description}
        </ColumnDescription>
      </ColumnInfo>

      <RowRightActions onClick={(e) => e.stopPropagation()}>
        <Tooltip title={isPinned ? 'Bỏ ghim cột' : 'Ghim cố định cột'}>
          <PinButton
            type="button"
            $pinned={isPinned}
            aria-label={isPinned ? 'Bỏ ghim cột' : 'Ghim cố định cột'}
            data-testid={`column-pin-${item.key}`}
            onClick={() => onTogglePin(item.key, !isPinned)}
          >
            {isPinned ? <PushpinFilled /> : <PushpinOutlined />}
          </PinButton>
        </Tooltip>

        <Tooltip
          title={
            isLocked
              ? 'Cột tên luôn hiển thị, không cho phép tắt'
              : item.visible
              ? 'Đang hiển thị (nhấn để ẩn)'
              : 'Đang ẩn (nhấn để hiển thị)'
          }
        >
          <SwitchWrapper>
            <Switch
              size="small"
              checked={isLocked ? true : item.visible}
              disabled={isLocked}
              data-testid={`column-switch-${item.key}`}
              onChange={(checked) => {
                if (!isLocked) {
                  onToggle(item.key, checked);
                }
              }}
            />
          </SwitchWrapper>
        </Tooltip>
      </RowRightActions>
    </ColumnRowItem>
  );
});

export interface ColumnSettingsPopoverProps {
  columns: CollectColumnConfigItem[];
  onChangeColumns: (nextColumns: CollectColumnConfigItem[]) => void;
  onResetColumns: () => void;
}

const ColumnSettingsPopover = ({
  columns,
  onChangeColumns,
  onResetColumns,
}: ColumnSettingsPopoverProps) => {
  const [open, setOpen] = useState(false);
  const [panelSize, setPanelSize] = useAtom(columnSettingsPanelSizeAtom);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 4,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const visibleCount = columns.filter((c) => c.visible || c.locked).length;
  const toggleableColumns = columns.filter((c) => !c.locked);
  const allVisible = toggleableColumns.every((c) => c.visible);
  const allHidden = toggleableColumns.every((c) => !c.visible);
  const hasCustomState =
    !allVisible || columns.some((c, idx) => Boolean(c.pinned) !== (idx === 0 && c.key === 'uuid'));

  const handleToggleColumn = (key: string, checked: boolean) => {
    const next = columns.map((col) =>
      col.key === key && !col.locked ? { ...col, visible: checked } : col,
    );
    onChangeColumns(next);
  };

  const handleTogglePin = (key: string, pinned: boolean) => {
    const next = columns.map((col) =>
      col.key === key ? { ...col, pinned } : col,
    );
    onChangeColumns(next);
  };

  const handleMoveColumn = (fromIndex: number, toIndex: number) => {
    if (fromIndex < 0 || toIndex < 0 || fromIndex >= columns.length || toIndex >= columns.length) {
      return;
    }
    onChangeColumns(arrayMove(columns, fromIndex, toIndex));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = columns.findIndex((c) => c.key === active.id);
      const newIndex = columns.findIndex((c) => c.key === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        onChangeColumns(arrayMove(columns, oldIndex, newIndex));
      }
    }
  };

  const handleShowAll = () => {
    onChangeColumns(columns.map((col) => ({ ...col, visible: true })));
  };

  const handleHideAll = () => {
    onChangeColumns(
      columns.map((col) => ({
        ...col,
        visible: col.locked ? true : false,
      })),
    );
  };

  const handleResizeStart = (
    e: React.MouseEvent<HTMLDivElement>,
    mode: 'vertical' | 'corner-left' | 'corner-right',
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = panelSize.width;
    const startHeight = panelSize.height;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaY = moveEvent.clientY - startY;
      const deltaX =
        mode === 'corner-left'
          ? startX - moveEvent.clientX
          : mode === 'corner-right'
          ? moveEvent.clientX - startX
          : 0;

      const maxH = Math.max(280, window.innerHeight - 36);
      const maxW = Math.max(360, Math.min(720, window.innerWidth - 40));

      setPanelSize({
        width: Math.min(maxW, Math.max(360, startWidth + deltaX)),
        height: Math.min(maxH, Math.max(260, startHeight + deltaY)),
      });
    };

    const handleMouseUp = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const helpTooltipContent = (
    <HelpTooltipBox>
      <div>• <b>Sắp xếp vị trí:</b> Kéo thả biểu tượng <b>⋮⋮</b> hoặc nhấn mũi tên lên/xuống.</div>
      <div>• <b>Ghim cột:</b> Nhấn biểu tượng ghim để cố định cột ở phía trái bảng khi cuộn ngang.</div>
      <div>• <b>Ẩn/hiện cột:</b> Bật/tắt công tắc ở mỗi dòng hoặc dùng nút <b>Bật hết / Tắt hết</b>.</div>
      <div>• <b>Cột tên:</b> Luôn được hiển thị cố định để nhận diện bản ghi, không thể tắt.</div>
      <div>• <b>Thay đổi kích thước:</b> Kéo cạnh dưới hoặc góc dưới của bảng cấu hình để mở rộng.</div>
    </HelpTooltipBox>
  );

  const popoverContent = (
    <PopoverContainer
      data-testid="column-settings-popover"
      style={{
        width: panelSize.width,
        height: panelSize.height,
      }}
    >
      <PopoverHeader>
        <HeaderTitleGroup>
          <SettingOutlined style={{ color: '#2563eb', fontSize: 14 }} />
          <HeaderTitle>Cấu hình cột hiển thị</HeaderTitle>
          <CountBadge data-testid="column-visible-count">
            {visibleCount}/{columns.length}
          </CountBadge>
          <Tooltip title={helpTooltipContent} placement="top" overlayStyle={{ maxWidth: 340 }}>
            <HelpIconButton
              type="button"
              aria-label="Hướng dẫn cấu hình cột"
              data-testid="column-settings-help-icon"
            >
              <QuestionCircleOutlined />
            </HelpIconButton>
          </Tooltip>
        </HeaderTitleGroup>

        <HeaderActions>
          <ActionLinkButton
            type="button"
            onClick={handleShowAll}
            disabled={allVisible}
            title="Hiển thị tất cả các cột"
            data-testid="btn-show-all-columns"
          >
            <EyeOutlined /> Bật hết
          </ActionLinkButton>
          <ActionLinkButton
            type="button"
            onClick={handleHideAll}
            disabled={allHidden}
            title="Ẩn tất cả các cột (trừ cột tên luôn hiển thị)"
            data-testid="btn-hide-all-columns"
          >
            <EyeInvisibleOutlined /> Tắt hết
          </ActionLinkButton>
          <ActionLinkButton
            type="button"
            onClick={onResetColumns}
            title="Khôi phục thứ tự, ghim và trạng thái hiển thị mặc định"
            data-testid="btn-reset-columns"
          >
            <ReloadOutlined /> Đặt lại
          </ActionLinkButton>
        </HeaderActions>
      </PopoverHeader>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={columns.map((c) => c.key)}
          strategy={verticalListSortingStrategy}
        >
          <ColumnList role="list" data-testid="column-settings-list">
            {columns.map((col, idx) => (
              <SortableColumnRow
                key={col.key}
                item={col}
                index={idx}
                total={columns.length}
                onToggle={handleToggleColumn}
                onTogglePin={handleTogglePin}
                onMove={handleMoveColumn}
              />
            ))}
          </ColumnList>
        </SortableContext>
      </DndContext>

      <ResizeFooterBar
        data-testid="column-settings-resize-handle"
        onMouseDown={(e) => handleResizeStart(e, 'vertical')}
        title="Kéo để thay đổi chiều cao bảng cấu hình"
      >
        <CornerResizeGripLeft
          onMouseDown={(e) => handleResizeStart(e, 'corner-left')}
          title="Kéo để thay đổi kích thước (rộng & cao)"
        />
        <ResizeGripIndicator />
        <CornerResizeGripRight
          onMouseDown={(e) => handleResizeStart(e, 'corner-right')}
          title="Kéo để thay đổi kích thước (rộng & cao)"
        />
      </ResizeFooterBar>
    </PopoverContainer>
  );

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      placement="left"
      autoAdjustOverflow
      open={open}
      onOpenChange={setOpen}
      overlayInnerStyle={{
        padding: 0,
        borderRadius: 10,
        overflow: 'hidden',
        boxShadow:
          '0 12px 28px -6px rgba(15, 23, 42, 0.18), 0 8px 12px -6px rgba(15, 23, 42, 0.08)',
        border: '1px solid #cbd5e1',
      }}
    >
      <Tooltip title="Cấu hình cột hiển thị" open={open ? false : undefined}>
        <Button
          type="text"
          size="small"
          icon={<SettingOutlined />}
          data-testid="btn-column-settings"
          style={
            open || hasCustomState
              ? { color: '#2563eb', background: '#eff6ff' }
              : undefined
          }
        />
      </Tooltip>
    </Popover>
  );
};

export default memo(ColumnSettingsPopover);

const PopoverContainer = styled.div`
  min-width: 360px;
  max-width: min(720px, 92vw);
  min-height: 260px;
  max-height: calc(100vh - 32px);
  background: #ffffff;
  display: flex;
  flex-direction: column;
  resize: both;
  overflow: hidden;
`;

const PopoverHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
  flex-wrap: wrap;
`;

const HeaderTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const HeaderTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
`;

const CountBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  font-family: monospace;
  color: #2563eb;
  background: #dbeafe;
  padding: 1px 7px;
  border-radius: 999px;
`;

const HelpIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 13px;
  padding: 2px;
  border-radius: 50%;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;

  &:hover {
    color: #2563eb;
    background: #eff6ff;
  }
`;

const HelpTooltipBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  line-height: 1.45;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ActionLinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11.5px;
  font-weight: 500;
  color: #2563eb;
  background: #ffffff;
  border: 1px solid #dbeafe;
  padding: 2px 7px;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: #eff6ff;
    border-color: #93c5fd;
    color: #1d4ed8;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    color: #94a3b8;
    border-color: #e2e8f0;
    background: #f8fafc;
  }
`;

const ColumnList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const ColumnRowItem = styled.div<{
  $visible: boolean;
  $isDragging: boolean;
  $isPinned: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 8px;
  background: ${(props) =>
    props.$isDragging
      ? '#eff6ff'
      : props.$isPinned
      ? '#f8faff'
      : props.$visible
      ? '#ffffff'
      : '#f8fafc'};
  border: 1px solid
    ${(props) =>
      props.$isDragging
        ? '#93c5fd'
        : props.$isPinned
        ? '#bfdbfe'
        : props.$visible
        ? '#e2e8f0'
        : '#f1f5f9'};
  box-shadow: ${(props) =>
    props.$isDragging ? '0 4px 12px rgba(37, 99, 235, 0.15)' : 'none'};
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
`;

const DragAndOrderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

const DragHandle = styled.div`
  cursor: grab;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 2px;
  font-size: 14px;

  &:hover {
    color: #475569;
  }

  &:active {
    cursor: grabbing;
  }
`;

const OrderArrowGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const OrderArrowBtn = styled.button`
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 9px;
  line-height: 1;
  padding: 2px 3px;
  border-radius: 3px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: #e2e8f0;
    color: #1e293b;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const TypeIconBadge = styled.div<{ $color: string }>`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: ${(props) => `${props.$color}14`};
  border: 1px solid ${(props) => `${props.$color}30`};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ColumnInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ColumnTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const ColumnTitle = styled.span<{ $visible: boolean }>`
  font-size: 12.5px;
  line-height: 1.4;
  font-weight: 600;
  color: ${(props) => (props.$visible ? '#1e293b' : '#94a3b8')};
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const TypeLabelTag = styled(Tag)`
  margin: 0 !important;
  font-size: 10px !important;
  line-height: 15px !important;
  padding: 0 5px !important;
  border-radius: 4px !important;
  flex-shrink: 0;
`;

const LockedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 600;
  color: #0f766e;
  background: #ccfbf1;
  border: 1px solid #99f6e4;
  padding: 0 5px;
  line-height: 15px;
  border-radius: 4px;
  flex-shrink: 0;
`;

const ColumnDescription = styled.div`
  font-size: 11px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RowRightActions = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PinButton = styled.button<{ $pinned: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 5px;
  border: 1px solid ${(props) => (props.$pinned ? '#93c5fd' : 'transparent')};
  background: ${(props) => (props.$pinned ? '#dbeafe' : 'transparent')};
  color: ${(props) => (props.$pinned ? '#2563eb' : '#94a3b8')};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: ${(props) => (props.$pinned ? '#1d4ed8' : '#475569')};
    background: ${(props) => (props.$pinned ? '#bfdbfe' : '#f1f5f9')};
    border-color: ${(props) => (props.$pinned ? '#60a5fa' : '#cbd5e1')};
  }
`;

const SwitchWrapper = styled.div`
  display: inline-flex;
  align-items: center;
`;

const ResizeFooterBar = styled.div`
  height: 12px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: ns-resize;
  flex-shrink: 0;
  user-select: none;
  padding: 0 4px;

  &:hover {
    background: #f1f5f9;
  }
`;

const ResizeGripIndicator = styled.div`
  width: 36px;
  height: 4px;
  border-radius: 999px;
  background: #cbd5e1;
`;

const CornerResizeGripLeft = styled.div`
  width: 14px;
  height: 10px;
  cursor: nesw-resize;
`;

const CornerResizeGripRight = styled.div`
  width: 14px;
  height: 10px;
  cursor: nwse-resize;
`;
